import React from 'react';
import { Button, Collapse, Space, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import type { PgaDisclaimRecommendationView } from 'libs/hooks/useComplianceRecommendation';
import { StyledAlert, ReasoningCollapse } from './styles';

const { Panel } = Collapse;

export interface PgaDisclaimRecommendationProps {
  view: PgaDisclaimRecommendationView;
  reason?: string;
  detailsLoading?: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
}

const REASONING_PLACEHOLDER = 'Reasoning will appear here.';

// Static — hoisted so it isn't recreated on every render.
const LOADING_DESCRIPTION = (
  <Space>
    <Spin indicator={<LoadingOutlined spin />} size="small" />
    <span>Fetching the AI disclaim recommendation…</span>
  </Space>
);

// Header is bold in the "Disclaim Recommended" card, normal weight elsewhere.
const Reasoning: React.FC<{
  reason?: string;
  loading?: boolean;
  bold?: boolean;
}> = ({ reason, loading, bold }) => (
  <ReasoningCollapse ghost $bold={bold}>
    <Panel key="reasoning" header="Reasoning">
      {loading ? 'Loading reasoning…' : reason || REASONING_PLACEHOLDER}
    </Panel>
  </ReasoningCollapse>
);

// PGA Disclaim AI card: the accept/decline prompt, accepted confirmation, or
// declined notice. Returns null for 'none' (caller shows the plain banner).
// Icons/colors come from antd's semantic Alert defaults (per `type`).
const PgaDisclaimRecommendation: React.FC<PgaDisclaimRecommendationProps> = ({
  view,
  reason,
  detailsLoading,
  onAccept,
  onDecline,
}) => {
  if (view === 'none') return null;

  if (view === 'loading') {
    return (
      <StyledAlert
        showIcon
        type="info"
        message="Checking disclaim recommendation"
        description={LOADING_DESCRIPTION}
      />
    );
  }

  if (view === 'error') {
    return (
      <StyledAlert
        showIcon
        type="error"
        message="Couldn't load disclaim recommendation"
        description="Please refresh or try again later."
      />
    );
  }

  if (view === 'accepted') {
    return (
      <StyledAlert
        showIcon
        $plainTitle
        type="success"
        message="Disclaim accepted"
        description={<Reasoning reason={reason} loading={detailsLoading} />}
      />
    );
  }

  if (view === 'declined') {
    return (
      <StyledAlert
        showIcon
        $plainTitle
        type="info"
        message="Disclaim recommendation declined"
      />
    );
  }

  // view === 'recommended'
  return (
    <StyledAlert
      showIcon
      type="info"
      message="Disclaim Recommended"
      description={
        <>
          Disclaiming this PGA is recommended. Click “Accept” to automatically
          apply disclaim. Click “Decline” to overwrite the disclaim
          recommendation and continue with filing.
          <Reasoning reason={reason} loading={detailsLoading} bold />
        </>
      }
      action={
        <Space direction="vertical">
          <Button size="small" onClick={onAccept}>
            Accept recommendation
          </Button>
          <Button size="small" onClick={onDecline}>
            Decline recommendation
          </Button>
        </Space>
      }
    />
  );
};

export default PgaDisclaimRecommendation;
